(() => {
  const systemLabels = {
    'US M': 'US(M)',
    'US W': 'US(W)',
    'US Kids': 'US(Kids)',
    UK: 'UK',
    EU: 'EU',
    CM: 'CM'
  };

  const normalizeValue = (value) => String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/⅓/g, '1/3')
    .replace(/⅔/g, '2/3')
    .replace(/½/g, '1/2')
    .replace(/\s+/g, '')
    .replace(/\.0$/, '');

  const parseCompositeSize = (value) => {
    const result = {};
    const source = String(value).toUpperCase()
      .replace(/⅓/g, ' 1/3').replace(/⅔/g, ' 2/3').replace(/½/g, ' 1/2');
    const size = '([0-9]+(?:\\.[0-9]+)?(?:\\s+[12]/3|\\s+1/2)?)';
    const capture = (system, expression) => {
      const match = source.match(new RegExp(expression));
      if (match) result[system] = match[1].trim();
    };
    capture('US Kids', `(?:US[-\\s]?(?:KIDS?|YOUTH))\\s*${size}`);
    capture('US W', `(?:US[-\\s]?(?:WOMAN|WOMEN|W)|(?:^|[/\\s])W)\\s*${size}`);
    capture('US M', `(?:US(?:[-\\s]?(?:MAN|MEN|M))?|(?:^|[/\\s])M)\\s*${size}`);
    capture('UK', `\\bUK\\s*${size}`);
    capture('EU', `\\b(?:EU|EUR)\\s*${size}`);
    capture('CM', `\\bCM\\s*${size}`);
    return result;
  };

  const detectChartKey = (root, sizeData) => {
    const explicit = String(root.dataset.sizeChartKey || '').trim().toLowerCase();
    if (explicit === 'nike-ja4' && sizeData.charts.nike) return 'nike';
    if (explicit === 'xtep-basketball' && sizeData.charts.xtep) return 'xtep';
    if (explicit === 'serious-player-only' && sizeData.charts.spo) return 'spo';
    if (explicit && explicit !== 'auto' && sizeData.charts[explicit]) return explicit;

    const template = String(root.dataset.templateSuffix || '').trim().toLowerCase();
    const haystack = [root.dataset.productVendor, root.dataset.productTitle, root.dataset.productTags]
      .filter(Boolean).join(' ').toLowerCase();
    const isRunning = /\b(run|runs|running|runner|runners|jog|jogging|marathon|trail)\b|跑步|跑鞋|马拉松|越野跑/.test(haystack);
    const templateKey = sizeData.templateMap[template];
    if (templateKey === 'xtep' && isRunning && sizeData.charts['xtep-running']) return 'xtep-running';
    if (templateKey) return templateKey;

    const isJordan = /\b(?:air\s+)?jordan\b/.test(haystack);
    if (isJordan && sizeData.charts.jordan) return 'jordan';
    const isNike = /(^|\W)nike(\W|$)/.test(haystack);
    if (isNike && sizeData.charts.nike) return 'nike';
    let baseKey = null;
    for (const [vendor, key] of Object.entries(sizeData.vendorMap || {})) {
      if (haystack.includes(vendor)) { baseKey = key; break; }
    }
    if (!baseKey) return null;

    const isKids = /\b(kid|kids|child|children|youth)\b|儿童|童鞋/.test(haystack);
    const isSlides = /\b(slide|slides|slipper|slippers)\b|拖鞋/.test(haystack);
    const family = baseKey.replace(/-(?:kids|slides|1|2)$/, '');
    if (family === 'xtep' && isRunning && sizeData.charts['xtep-running']) return 'xtep-running';
    if (isKids && sizeData.charts[`${family}-kids`]) return `${family}-kids`;
    if (isSlides && sizeData.charts[`${family}-slides`]) return `${family}-slides`;
    return sizeData.charts[baseKey] ? baseKey : null;
  };

  const findChartRow = (chart, rawValue, sourceSystem) => {
    const composite = parseCompositeSize(rawValue);
    for (const [system, value] of Object.entries(composite)) {
      const row = chart.rows.find((candidate) => normalizeValue(candidate[system]) === normalizeValue(value));
      if (row) return row;
    }

    const direct = chart.rows.find((row) => normalizeValue(row[sourceSystem]) === normalizeValue(rawValue));
    if (direct) return direct;

    const matches = chart.rows.filter((row) => chart.systems.some((system) =>
      normalizeValue(row[system]) === normalizeValue(rawValue)
    ));
    return matches.length === 1 ? matches[0] : null;
  };

  const displaySize = (rawValue, sourceSystem, targetSystem, chart) => {
    const row = findChartRow(chart, rawValue, sourceSystem);
    if (row && (row[targetSystem] === undefined || row[targetSystem] === null || row[targetSystem] === '')) {
      return `${systemLabels[targetSystem] || targetSystem} —`;
    }
    const value = row?.[targetSystem];
    if (value === undefined || value === null || value === '') return String(rawValue);
    return `${systemLabels[targetSystem] || targetSystem} ${value}`;
  };

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: currency || 'USD'
      }).format(Number(cents) / 100);
    } catch (_) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const renderGuideTable = (root, chart) => {
    const guideId = root.querySelector('[data-ks-size-guide]')?.getAttribute('aria-controls');
    const modal = guideId ? document.getElementById(guideId) : null;
    const container = modal?.querySelector('[data-ks-size-guide-table]');
    if (!modal || !container || container.dataset.ksReady === 'true') return;
    container.dataset.ksReady = 'true';

    const title = modal.querySelector('[data-ks-size-guide-title]');
    if (title) title.textContent = chart.label || 'Size Guide';

    const buildTable = (tableData) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'ks-size-guide-table-wrap';
      if (tableData.label) {
        const heading = document.createElement('h3');
        heading.className = 'ks-size-guide-table-title';
        heading.textContent = tableData.label;
        wrapper.appendChild(heading);
      }
      const scroller = document.createElement('div');
      scroller.className = 'ks-size-guide-table-scroll';
      const table = document.createElement('table');
      table.className = 'ks-size-guide-table';
      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      tableData.systems.forEach((system) => {
        const cell = document.createElement('th');
        cell.scope = 'col';
        cell.textContent = tableData.displayLabels?.[system] || systemLabels[system] || system;
        headRow.appendChild(cell);
      });
      thead.appendChild(headRow);
      const tbody = document.createElement('tbody');
      tableData.rows.forEach((row) => {
        const tableRow = document.createElement('tr');
        tableData.systems.forEach((system) => {
          const cell = document.createElement('td');
          cell.textContent = row[system] ?? '—';
          tableRow.appendChild(cell);
        });
        tbody.appendChild(tableRow);
      });
      table.append(thead, tbody);
      scroller.appendChild(table);
      wrapper.appendChild(scroller);
      return wrapper;
    };

    const tables = chart.tables || [chart];
    container.replaceChildren(...tables.map(buildTable));
    modal.querySelector('[data-ks-size-guide-fallback]')?.setAttribute('hidden', '');
  };

  const init = (root) => {
    if (root.dataset.ksReady === 'true') return;
    root.dataset.ksReady = 'true';

    const sizeData = window.KicksownSizeData;
    const chartKey = sizeData ? detectChartKey(root, sizeData) : null;
    const chart = chartKey ? sizeData.charts[chartKey] : null;
    const productVariants = root.closest('product-variants');
    if (!chart || !productVariants) {
      root.remove();
      return;
    }

    root.dataset.resolvedSizeChart = chartKey;
    const toggle = root.querySelector('[data-ks-size-toggle]');
    const panel = root.querySelector('[data-ks-size-panel]');
    const tabs = root.querySelector('[data-ks-size-tabs]');
    const list = root.querySelector('[data-ks-size-list]');
    const empty = root.querySelector('[data-ks-size-empty]');
    const current = root.querySelector('[data-ks-size-current]');
    const optionPosition = Number(root.dataset.optionPosition);
    const enabledSystems = String(root.dataset.enabledSystems || '').split(',').map((item) => item.trim()).filter(Boolean);
    const visibleSystems = chart.systems.filter((system) => enabledSystems.includes(system));
    const systems = visibleSystems.length ? visibleSystems : chart.systems;
    const requestedSource = String(root.dataset.baseSystem || '').trim();
    const sourceSystem = chart.systems.includes(requestedSource) ? requestedSource : chart.systems[0];
    let activeSystem = systems.includes(sourceSystem) ? sourceSystem : systems[0];
    let variants = [];

    try {
      variants = JSON.parse(root.querySelector('[data-ks-variants]').textContent);
    } catch (_) {
      variants = [];
    }

    const controls = () => Array.from(productVariants.querySelectorAll(`[name="option${optionPosition}"]`));
    const currentOptions = () => Array.from(
      productVariants.querySelectorAll('input[name^="option"]:checked, select[name^="option"]')
    ).map((control) => control.value);
    const controlFor = (value) => controls().find((control) => control.value === value);

    if (!variants.length || !controls().length) {
      root.remove();
      return;
    }

    tabs.replaceChildren();
    systems.forEach((system) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.role = 'tab';
      tab.className = 'ks-size-picker__tab';
      tab.dataset.sizeSystem = system;
      tab.setAttribute('aria-selected', String(system === activeSystem));
      tab.textContent = systemLabels[system] || system;
      tab.addEventListener('click', () => {
        activeSystem = system;
        tabs.querySelectorAll('[data-size-system]').forEach((item) =>
          item.setAttribute('aria-selected', String(item === tab))
        );
        render();
      });
      tabs.appendChild(tab);
    });

    root.hidden = false;
    renderGuideTable(root, chart);

    const selectValue = (value) => {
      const control = controlFor(value);
      if (!control) return;
      if (control.tagName === 'SELECT') control.value = value;
      else control.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      current.textContent = displaySize(value, sourceSystem, activeSystem, chart);
      root.classList.add('has-selection');
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      window.dataLayer?.push({ event: 'size_selected', size_option: value, size_system: activeSystem, size_chart: chartKey });
    };

    const render = () => {
      const selected = currentOptions();
      const relevant = variants.filter((variant) => variant.options.every((value, index) =>
        index === optionPosition - 1 || !selected[index] || value === selected[index]
      ));
      const bySize = new Map();
      relevant.forEach((variant) => {
        const size = variant.options[optionPosition - 1];
        const previous = bySize.get(size);
        if (!previous || (!previous.available && variant.available) || variant.price < previous.price) bySize.set(size, variant);
      });

      list.replaceChildren();
      const selectedValue = selected[optionPosition - 1];
      bySize.forEach((variant, rawSize) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'ks-size-picker__row';
        row.disabled = !variant.available;
        row.setAttribute('aria-pressed', String(rawSize === selectedValue));

        const price = document.createElement('span');
        price.className = variant.available ? 'ks-size-picker__price' : 'ks-size-picker__sold';
        price.textContent = variant.available ? money(variant.price, window.Shopify?.currency?.active) : 'Sold out';

        const label = document.createElement('span');
        label.className = 'ks-size-picker__label';
        label.textContent = displaySize(rawSize, sourceSystem, activeSystem, chart);
        row.append(price, label);
        row.addEventListener('click', () => selectValue(rawSize));
        list.appendChild(row);
      });
      empty.hidden = bySize.size > 0;
      if (selectedValue) {
        current.textContent = displaySize(selectedValue, sourceSystem, activeSystem, chart);
        root.classList.add('has-selection');
      }
    };

    toggle.addEventListener('click', () => {
      const opening = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(opening));
      panel.hidden = !opening;
      if (opening) render();
    });

    root.querySelector('[data-ks-size-guide]')?.addEventListener('click', () => {
      const modalId = root.querySelector('[data-ks-size-guide]').getAttribute('aria-controls');
      const modal = modalId ? document.getElementById(modalId) : null;
      if (!modal) return;
      modal.hidden = false;
      document.documentElement.classList.add('ks-size-guide-open');
      modal.querySelector('[data-ks-size-guide-close]')?.focus();
    });

    const initial = currentOptions()[optionPosition - 1];
    if (initial) {
      current.textContent = displaySize(initial, sourceSystem, activeSystem, chart);
      root.classList.add('has-selection');
    }
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-ks-size-picker]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ks-size-guide-trigger]');
    if (trigger) {
      const modal = document.getElementById(trigger.getAttribute('aria-controls'));
      if (modal) {
        modal.hidden = false;
        document.documentElement.classList.add('ks-size-guide-open');
        modal.querySelector('[data-ks-size-guide-close]')?.focus();
      }
      return;
    }
    const close = event.target.closest('[data-ks-size-guide-close]');
    const modal = close?.closest('[data-ks-size-guide-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('ks-size-guide-open');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('[data-ks-size-guide-modal]:not([hidden])');
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('ks-size-guide-open');
  });
})();
